import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit, Delete, Close, Add } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface PriceCategory {
  externalId: string;
  solutionCategoryId: string;
  priceMin: number;
  priceMax: number;
  solutionCategory: {
    externalId: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

const PriceCategoryPage: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<PriceCategory[]>([]);
  const [solutionCategories, setSolutionCategories] = useState<Array<{
    externalId: string;
    name: string;
  }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingCategory, setDeletingCategory] = useState<PriceCategory | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<PriceCategory | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState({
    solutionCategoryId: '',
    priceMin: '',
    priceMax: '',
  });
  const [addFormData, setAddFormData] = useState({
    solutionCategoryId: '',
    priceMin: '',
    priceMax: '',
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [priceCategoriesResponse, solutionCategoriesResponse] = await Promise.all([
          apiService.getPriceCategories(),
          apiService.getSolutionCategories(),
        ]);
        
        setCategories(priceCategoriesResponse.items);
        setSolutionCategories(solutionCategoriesResponse.items);
      } catch (err: any) {
        console.error('Error loading price categories:', err);
        setError(err.message || t('priceCategory.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const handleDeleteClick = (category: PriceCategory) => {
    setDeletingCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingCategory(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCategory) return;

    try {
      setDeleting(true);
      await apiService.deletePriceCategory(deletingCategory.externalId);
      
      // Remove category from list
      setCategories(prev => prev.filter(c => c.externalId !== deletingCategory.externalId));
      
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting price category:', err);
      setError(err.message || t('priceCategory.errors.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = (category: PriceCategory) => {
    setEditingCategory(category);
    setEditFormData({
      solutionCategoryId: category.solutionCategoryId,
      priceMin: category.priceMin.toString(),
      priceMax: category.priceMax.toString(),
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCategory(null);
    setEditFormData({
      solutionCategoryId: '',
      priceMin: '',
      priceMax: '',
    });
  };

  const handleEditInputChange = (field: keyof typeof editFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleEditSelectChange = (event: any) => {
    setEditFormData(prev => ({
      ...prev,
      solutionCategoryId: event.target.value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingCategory) return;

    // Validate required fields
    if (!editFormData.solutionCategoryId || !editFormData.priceMin || !editFormData.priceMax) {
      setError(t('priceCategory.errors.fillRequiredFields'));
      return;
    }

    const priceMin = parseFloat(editFormData.priceMin);
    const priceMax = parseFloat(editFormData.priceMax);

    if (isNaN(priceMin) || isNaN(priceMax)) {
      setError(t('priceCategory.errors.invalidNumbers'));
      return;
    }

    if (priceMin < 0 || priceMax < 0) {
      setError(t('priceCategory.errors.mustBePositive'));
      return;
    }

    if (priceMin > priceMax) {
      setError(t('priceCategory.errors.minGreaterThanMax'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const updatedCategory = await apiService.updatePriceCategory(editingCategory.externalId, {
        solutionCategoryId: editFormData.solutionCategoryId,
        priceMin,
        priceMax,
      });
      
      // Update category in list
      setCategories(prev => 
        prev.map(c => c.externalId === editingCategory.externalId ? updatedCategory : c)
      );

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating price category:', err);
      setError(err.message || t('priceCategory.errors.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClick = () => {
    setAddFormData({
      solutionCategoryId: '',
      priceMin: '',
      priceMax: '',
    });
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setAddFormData({
      solutionCategoryId: '',
      priceMin: '',
      priceMax: '',
    });
  };

  const handleAddInputChange = (field: keyof typeof addFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAddFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddSelectChange = (event: any) => {
    setAddFormData(prev => ({
      ...prev,
      solutionCategoryId: event.target.value,
    }));
  };

  const handleSaveAdd = async () => {
    // Validate required fields
    if (!addFormData.solutionCategoryId || !addFormData.priceMin || !addFormData.priceMax) {
      setError(t('priceCategory.errors.fillRequiredFields'));
      return;
    }

    const priceMin = parseFloat(addFormData.priceMin);
    const priceMax = parseFloat(addFormData.priceMax);

    if (isNaN(priceMin) || isNaN(priceMax)) {
      setError(t('priceCategory.errors.invalidNumbers'));
      return;
    }

    if (priceMin < 0 || priceMax < 0) {
      setError(t('priceCategory.errors.mustBePositive'));
      return;
    }

    if (priceMin > priceMax) {
      setError(t('priceCategory.errors.minGreaterThanMax'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const newCategory = await apiService.createPriceCategory({
        solutionCategoryId: addFormData.solutionCategoryId,
        priceMin,
        priceMax,
      });
      
      // Add category to list
      setCategories(prev => [...prev, newCategory]);

      handleCloseAddModal();
    } catch (err: any) {
      console.error('Error creating price category:', err);
      setError(err.message || t('priceCategory.errors.failedToCreate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('priceCategory.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddClick}
        >
          {t('priceCategory.addCategory')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('priceCategory.table.category')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('priceCategory.table.minPrice')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('priceCategory.table.maxPrice')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('priceCategory.table.createdAt')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('priceCategory.table.updatedAt')}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6">{t('priceCategory.table.actions')}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('priceCategory.noCategoriesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map(category => (
                    <TableRow key={category.externalId} hover>
                      <TableCell>{category.solutionCategory.name}</TableCell>
                      <TableCell>{category.priceMin} {t('priceCategory.credits')}</TableCell>
                      <TableCell>{category.priceMax} {t('priceCategory.credits')}</TableCell>
                      <TableCell>{formatDate(category.createdAt)}</TableCell>
                      <TableCell>{formatDate(category.updatedAt)}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title={t('priceCategory.tooltips.edit')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(category)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('priceCategory.tooltips.delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(category)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('priceCategory.modals.delete.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('priceCategory.modals.delete.confirm')} "{deletingCategory?.solutionCategory.name}" ({deletingCategory?.priceMin} - {deletingCategory?.priceMax} {t('priceCategory.credits')})? {t('priceCategory.modals.delete.warning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="secondary" disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? t('priceCategory.modals.delete.deleting') : t('priceCategory.modals.delete.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Price Category Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('priceCategory.modals.edit.title')}</Typography>
            <IconButton onClick={handleCloseEditModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('priceCategory.modals.edit.solutionCategory')}</InputLabel>
              <Select
                label={t('priceCategory.modals.edit.solutionCategory')}
                value={editFormData.solutionCategoryId}
                onChange={handleEditSelectChange}
              >
                {solutionCategories.map(category => (
                  <MenuItem key={category.externalId} value={category.externalId}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('priceCategory.modals.edit.minPrice')}</InputLabel>
              <OutlinedInput
                type="number"
                value={editFormData.priceMin}
                onChange={handleEditInputChange('priceMin')}
                label={t('priceCategory.modals.edit.minPrice')}
                inputProps={{ min: 0, step: 1 }}
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('priceCategory.modals.edit.maxPrice')}</InputLabel>
              <OutlinedInput
                type="number"
                value={editFormData.priceMax}
                onChange={handleEditInputChange('priceMax')}
                label={t('priceCategory.modals.edit.maxPrice')}
                inputProps={{ min: 0, step: 1 }}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary" disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveEdit}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t('priceCategory.modals.edit.saving') : t('priceCategory.modals.edit.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Price Category Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('priceCategory.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseAddModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('priceCategory.modals.add.solutionCategory')}</InputLabel>
              <Select
                label={t('priceCategory.modals.add.solutionCategory')}
                value={addFormData.solutionCategoryId}
                onChange={handleAddSelectChange}
              >
                {solutionCategories.map(category => (
                  <MenuItem key={category.externalId} value={category.externalId}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('priceCategory.modals.add.minPrice')}</InputLabel>
              <OutlinedInput
                type="number"
                value={addFormData.priceMin}
                onChange={handleAddInputChange('priceMin')}
                label={t('priceCategory.modals.add.minPrice')}
                inputProps={{ min: 0, step: 1 }}
              />
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('priceCategory.modals.add.maxPrice')}</InputLabel>
              <OutlinedInput
                type="number"
                value={addFormData.priceMax}
                onChange={handleAddInputChange('priceMax')}
                label={t('priceCategory.modals.add.maxPrice')}
                inputProps={{ min: 0, step: 1 }}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAddModal} color="secondary" disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? t('priceCategory.modals.add.creating') : t('priceCategory.modals.add.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceCategoryPage;

